import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import {
  CheckCircle2Icon,
  LogOutIcon,
  SendIcon,
} from "lucide-react";
import { useState } from "react";
import {
  useWhatsAppWorkerLogout,
  useWhatsAppWorkerStatusQuery,
} from "../api/queries";
import { WorkerTestSendDialog } from "./WorkerTestSendDialog";

function formatConnectedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("lo-LA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function WhatsAppWorkerConnectedPanel() {
  const workerQuery = useWhatsAppWorkerStatusQuery({ refetchInterval: 3_000 });
  const logout = useWhatsAppWorkerLogout();
  const [testOpen, setTestOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const isConnected =
    workerQuery.data?.connected === true ||
    workerQuery.data?.status === "connected";

  if (!isConnected) return null;

  const connectedAt = formatConnectedAt(workerQuery.data?.lastConnectedAt);

  const handleLogout = async () => {
    try {
      const result = await logout.mutateAsync();
      if (!result.ok) {
        toast.error(result.error ?? "Logout ບໍ່ສຳເລັດ");
        return;
      }
      toast.success("ອອກຈາກ session ແລ້ວ — ສະແກນ QR ໃໝ່");
      setLogoutOpen(false);
    } catch {
      toast.error("Logout ບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Card className="gap-0 border-emerald-500/30 bg-emerald-500/[0.02]">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="size-5 shrink-0 text-emerald-600" />
              <CardTitle className="text-base">WhatsApp ເຊື່ອມຕໍ່ແລ້ວ</CardTitle>
            </div>
            <CardDescription className="mt-1">
              {connectedAt
                ? `ເຊື່ອມຄັ້ງລ່າສຸດ: ${connectedAt}`
                : "ພ້ອມສົ່ງຂໍ້ຄວາມ"}
              {workerQuery.data?.messagesToday != null
                ? ` · ສົ່ງມື້ນີ້ ${workerQuery.data.messagesToday} ຂໍ້ຄວາມ`
                : null}
            </CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setTestOpen(true)}
            >
              <SendIcon className="mr-1 size-4" />
              ທົດສອບສົ່ງ
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              onClick={() => setLogoutOpen(true)}
            >
              <LogOutIcon className="mr-1 size-4" />
              Logout session
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 text-muted-foreground text-sm">
          Logout ຈະຕັດການເຊື່ອມ Linked Device ແລະຕ້ອງ scan QR ໃໝ່
        </CardContent>
      </Card>

      <WorkerTestSendDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        onSuccess={() => toast.success("ສົ່ງຂໍ້ຄວາມທົດສອບສຳເລັດ")}
        onError={(msg) => toast.error(msg)}
      />

      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Logout WhatsApp session?</AlertDialogTitle>
            <AlertDialogDescription>
              ຈະຕັດການເຊື່ອມອຸປະກອນນີ້ອອກຈາກ WhatsApp Studio ແລະຕ້ອງ scan QR
              ໃໝ່ກ່ອນສົ່ງຂໍ້ຄວາມອີກຄັ້ງ
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={logout.isPending}>
              ຍົກເລີກ
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={logout.isPending}
              onClick={(e) => {
                e.preventDefault();
                void handleLogout();
              }}
            >
              {logout.isPending ? "ກຳລັງ logout..." : "ຢືນຢັນ logout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
